import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

// Stimulus controller for recording match video via MediaRecorder API
// and uploading via Active Storage Direct Upload.
//
// Phase 1 improvements for robustness:
// - Wake Lock API to prevent screen sleep during recording
// - Chunked upload every 30 seconds to prevent memory overflow
// - IndexedDB backup for crash recovery
// - Supports landscape/fullscreen mode for better court filming on mobile.

// IndexedDB storage for chunk backup
class ChunkStorage {
  constructor(dbName = 'matchRecorderChunks') {
    this.dbName = dbName
    this.storeName = 'chunks'
    this.db = null
  }

  async open() {
    if (this.db) return this.db

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, 1)

      request.onerror = () => reject(request.error)
      request.onsuccess = () => {
        this.db = request.result
        resolve(this.db)
      }

      request.onupgradeneeded = (event) => {
        const db = event.target.result
        if (!db.objectStoreNames.contains(this.storeName)) {
          const store = db.createObjectStore(this.storeName, { keyPath: 'id', autoIncrement: true })
          store.createIndex('recordingId', 'recordingId', { unique: false })
        }
      }
    })
  }

  async saveChunk(recordingId, chunkIndex, blob) {
    await this.open()
    const transaction = this.db.transaction([this.storeName], 'readwrite')
    const store = transaction.objectStore(this.storeName)

    return new Promise((resolve, reject) => {
      const request = store.add({
        recordingId,
        chunkIndex,
        blob,
        timestamp: Date.now(),
        uploaded: false
      })
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
  }

  async markChunkUploaded(id) {
    await this.open()
    const transaction = this.db.transaction([this.storeName], 'readwrite')
    const store = transaction.objectStore(this.storeName)

    return new Promise((resolve, reject) => {
      const getRequest = store.get(id)
      getRequest.onsuccess = () => {
        const chunk = getRequest.result
        if (chunk) {
          chunk.uploaded = true
          const putRequest = store.put(chunk)
          putRequest.onsuccess = () => resolve()
          putRequest.onerror = () => reject(putRequest.error)
        } else {
          resolve()
        }
      }
      getRequest.onerror = () => reject(getRequest.error)
    })
  }

  async getPendingChunks(recordingId) {
    await this.open()
    const transaction = this.db.transaction([this.storeName], 'readonly')
    const store = transaction.objectStore(this.storeName)

    return new Promise((resolve, reject) => {
      const request = store.getAll()
      request.onsuccess = () => {
        const chunks = request.result
          .filter(c => c.recordingId === recordingId && !c.uploaded)
          .sort((a, b) => a.chunkIndex - b.chunkIndex)
        resolve(chunks)
      }
      request.onerror = () => reject(request.error)
    })
  }

  async clearChunks(recordingId) {
    await this.open()
    const transaction = this.db.transaction([this.storeName], 'readwrite')
    const store = transaction.objectStore(this.storeName)

    return new Promise((resolve, reject) => {
      const request = store.getAll()
      request.onsuccess = () => {
        const toDelete = request.result.filter(c => c.recordingId === recordingId)
        toDelete.forEach(chunk => store.delete(chunk.id))
        resolve()
      }
      request.onerror = () => reject(request.error)
    })
  }
}

export default class extends Controller {
  static targets = ["preview", "startBtn", "stopBtn", "timer", "status", "progress", "progressBar", "landscapeBtn", "container", "chunkStatus"]
  static values = {
    matchId: { type: Number, default: 0 },
    createUrl: String,
    standaloneUrl: { type: String, default: "" },
    directUploadUrl: String,
    chunkUploadUrl: { type: String, default: "/joueurs/recordings/chunk" },
    chunkIntervalMs: { type: Number, default: 30000 }, // Upload every 30 seconds
    immersive: { type: Boolean, default: false }
  }

  connect() {
    this.mediaRecorder = null
    this.recordedChunks = []
    this.stream = null
    this.timerInterval = null
    this.startTime = null
    this.recordingId = null
    this.isLandscape = false

    // Chunked upload state
    this.chunkIndex = 0
    this.pendingChunks = []
    this.isUploading = false
    this.uploadedChunkCount = 0
    this.chunkStorage = new ChunkStorage()

    // Wake Lock state
    this.wakeLock = null

    // Listen for fullscreen exit (e.g. user presses Escape)
    this._onFullscreenChange = () => {
      if (!document.fullscreenElement && !document.webkitFullscreenElement) {
        this._onExitedFullscreen()
      }
    }
    document.addEventListener("fullscreenchange", this._onFullscreenChange)
    document.addEventListener("webkitfullscreenchange", this._onFullscreenChange)

    // Listen for visibility change to re-acquire wake lock
    this._onVisibilityChange = () => this._reacquireWakeLock()
    document.addEventListener("visibilitychange", this._onVisibilityChange)

    // In immersive mode, apply fullscreen CSS immediately (no browser chrome needed)
    if (this.immersiveValue) {
      this.element.classList.add("recorder-fullscreen")
    }
  }

  disconnect() {
    this._stopStream()
    this._clearTimer()
    this._exitLandscape()
    this._releaseWakeLock()
    document.removeEventListener("fullscreenchange", this._onFullscreenChange)
    document.removeEventListener("webkitfullscreenchange", this._onFullscreenChange)
    document.removeEventListener("visibilitychange", this._onVisibilityChange)
  }

  async startRecording() {
    try {
      this.statusTarget.textContent = "Demande d'accès caméra..."

      // Request Wake Lock to prevent screen sleep
      await this._requestWakeLock()

      this.stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: true
      })

      this.previewTarget.srcObject = this.stream

      // Reset chunked recording state
      this.recordedChunks = []
      this.pendingChunks = []
      this.chunkIndex = 0
      this.uploadedChunkCount = 0
      this.isUploading = false

      // Create the recording on the server first to get recordingId
      await this._createRecordingOnServer()

      const mimeType = this._preferredMimeType()
      this.mediaRecorder = new MediaRecorder(this.stream, {
        mimeType,
        videoBitsPerSecond: 2_500_000
      })

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          this.pendingChunks.push(event.data)

          // Check if we should upload (every ~30 seconds = 30 chunks at 1s interval)
          const chunksPerUpload = Math.floor(this.chunkIntervalMsValue / 1000)
          if (this.pendingChunks.length >= chunksPerUpload) {
            this._uploadPendingChunks()
          }
        }
      }

      this.mediaRecorder.onstop = () => this._onRecordingStopped()

      this.mediaRecorder.start(1000) // Collect data every second

      this.startBtnTarget.disabled = true
      this.stopBtnTarget.disabled = false
      this.timerTarget.classList.remove("hidden")
      if (this.hasLandscapeBtnTarget && !this.immersiveValue) {
        this.landscapeBtnTarget.classList.remove("hidden")
      }
      this.statusTarget.textContent = "Enregistrement en cours..."

      // In immersive mode, request browser fullscreen (user gesture from Rec click)
      if (this.immersiveValue) {
        this._requestBrowserFullscreen()
      }
      this._updateChunkStatus()

      this._startTimer()
    } catch (error) {
      console.error("Error starting recording:", error)
      this.statusTarget.textContent = `Erreur : ${this._errorMessage(error)}`
      this._releaseWakeLock()
    }
  }

  // Create the recording on the server before starting to capture
  async _createRecordingOnServer() {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    const isStandalone = !this.matchIdValue || this.matchIdValue === 0
    const url = isStandalone ? this.standaloneUrlValue : this.createUrlValue

    const createResponse = await this._fetchWithTimeout(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken
      },
      body: JSON.stringify({ chunked: true })
    }, 15000)

    if (!createResponse.ok) {
      let errMsg = `Erreur serveur (${createResponse.status})`
      try {
        const errData = await createResponse.json()
        errMsg = errData.error || errMsg
      } catch (e) { /* response wasn't JSON */ }
      throw new Error(errMsg)
    }

    const { id, share_token, direct_upload_url } = await createResponse.json()
    this.recordingId = id
    this.shareToken = share_token
    this.directUploadUrl = direct_upload_url
  }

  // Wake Lock API methods
  async _requestWakeLock() {
    if ('wakeLock' in navigator) {
      try {
        this.wakeLock = await navigator.wakeLock.request('screen')
        console.log('Wake Lock activé - l\'écran restera allumé')
      } catch (e) {
        console.warn('Wake Lock non disponible:', e.message)
      }
    } else {
      console.warn('Wake Lock API non supportée par ce navigateur')
    }
  }

  async _reacquireWakeLock() {
    if (this.wakeLock !== null && document.visibilityState === 'visible' && this.mediaRecorder?.state === 'recording') {
      try {
        this.wakeLock = await navigator.wakeLock.request('screen')
        console.log('Wake Lock réacquis après retour au premier plan')
      } catch (e) {
        console.warn('Impossible de réacquérir le Wake Lock:', e.message)
      }
    }
  }

  _releaseWakeLock() {
    if (this.wakeLock) {
      this.wakeLock.release().then(() => {
        console.log('Wake Lock libéré')
      }).catch(() => {})
      this.wakeLock = null
    }
  }

  // Chunked upload methods
  async _uploadPendingChunks() {
    if (this.pendingChunks.length === 0 || this.isUploading || !this.recordingId) return

    this.isUploading = true
    const chunksToUpload = [...this.pendingChunks]
    this.pendingChunks = []

    const mimeType = this._preferredMimeType()
    const chunkBlob = new Blob(chunksToUpload, { type: mimeType })
    const currentChunkIndex = this.chunkIndex++

    try {
      // Save to IndexedDB first for backup
      const dbId = await this.chunkStorage.saveChunk(this.recordingId, currentChunkIndex, chunkBlob)

      // Upload the chunk
      await this._uploadSingleChunk(chunkBlob, currentChunkIndex)

      // Mark as uploaded in IndexedDB
      await this.chunkStorage.markChunkUploaded(dbId)

      this.uploadedChunkCount++
      this._updateChunkStatus()

      // Release memory
      chunksToUpload.length = 0
    } catch (error) {
      console.error(`Chunk ${currentChunkIndex} upload failed, will retry:`, error)
      // Put chunks back for retry (will be uploaded with next batch or at the end)
      this.pendingChunks = [...chunksToUpload, ...this.pendingChunks]
      this.chunkIndex-- // Reset index since upload failed
    }

    this.isUploading = false
  }

  async _uploadSingleChunk(blob, index) {
    const formData = new FormData()
    formData.append('chunk', blob)
    formData.append('index', index)
    formData.append('recording_id', this.recordingId)

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    const response = await this._fetchWithTimeout(this.chunkUploadUrlValue, {
      method: 'POST',
      headers: { 'X-CSRF-Token': csrfToken },
      body: formData
    }, 60000)

    if (!response.ok) {
      throw new Error(`Chunk ${index} upload failed: ${response.status}`)
    }

    return response.json()
  }

  _updateChunkStatus() {
    if (this.hasChunkStatusTarget) {
      const pendingSize = this.pendingChunks.reduce((acc, chunk) => acc + chunk.size, 0)
      const pendingMB = (pendingSize / (1024 * 1024)).toFixed(1)
      this.chunkStatusTarget.textContent = `${this.uploadedChunkCount} segments uploadés | ${pendingMB} MB en attente`
    }
  }

  stopRecording() {
    if (this.mediaRecorder && this.mediaRecorder.state !== "inactive") {
      this.mediaRecorder.stop()
    }
    this._stopStream()
    this._clearTimer()
    this._exitLandscape()
    this._releaseWakeLock()
    this.stopBtnTarget.disabled = true
    this.startBtnTarget.disabled = true // both disabled during upload
    if (this.hasLandscapeBtnTarget) {
      this.landscapeBtnTarget.classList.add("hidden")
    }
    this.statusTarget.textContent = "Upload des derniers segments..."
  }

  // Toggle landscape/fullscreen mode for better court filming
  toggleLandscape() {
    if (this.isLandscape) {
      this._exitLandscape()
    } else {
      this._enterLandscape()
    }
  }

  async _enterLandscape() {
    try {
      const container = this.hasContainerTarget ? this.containerTarget : this.element

      // Request fullscreen
      if (container.requestFullscreen) {
        await container.requestFullscreen()
      } else if (container.webkitRequestFullscreen) {
        await container.webkitRequestFullscreen()
      }

      // Try to lock orientation to landscape (mobile only)
      if (screen.orientation && screen.orientation.lock) {
        try {
          await screen.orientation.lock("landscape")
        } catch (e) {
          // Orientation lock may not be supported — fullscreen still works
        }
      }

      this.isLandscape = true
      this.element.classList.add("recorder-fullscreen")
      if (this.hasLandscapeBtnTarget) {
        this.landscapeBtnTarget.innerHTML = '<svg class="w-5 h-5 mr-1.5" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M9 9V4.5M9 9H4.5M9 9 3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5 5.25 5.25"/></svg><span>Quitter</span>'
      }
    } catch (e) {
      console.log("Fullscreen not available:", e.message)
    }
  }

  // Request browser fullscreen for immersive mode (called from user gesture)
  async _requestBrowserFullscreen() {
    try {
      const el = document.documentElement
      if (el.requestFullscreen) {
        await el.requestFullscreen()
      } else if (el.webkitRequestFullscreen) {
        await el.webkitRequestFullscreen()
      }

      // Try to lock orientation to landscape on mobile
      if (screen.orientation && screen.orientation.lock) {
        try {
          await screen.orientation.lock("landscape")
        } catch (e) {
          // Orientation lock may not be supported
        }
      }
    } catch (e) {
      console.log("Browser fullscreen not available:", e.message)
    }
  }

  _exitLandscape() {
    if (!this.isLandscape) return

    this.element.classList.remove("recorder-fullscreen")

    if (document.fullscreenElement || document.webkitFullscreenElement) {
      if (document.exitFullscreen) {
        document.exitFullscreen().catch(() => {})
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen()
      }
    }

    if (screen.orientation && screen.orientation.unlock) {
      try { screen.orientation.unlock() } catch (e) { /* ignore */ }
    }

    this.isLandscape = false
    if (this.hasLandscapeBtnTarget) {
      this.landscapeBtnTarget.innerHTML = '<svg class="w-5 h-5 mr-1.5" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15"/></svg><span>Mode paysage</span>'
    }
  }

  _onExitedFullscreen() {
    if (this.isLandscape) {
      this.isLandscape = false
      this.element.classList.remove("recorder-fullscreen")
      if (screen.orientation && screen.orientation.unlock) {
        try { screen.orientation.unlock() } catch (e) { /* ignore */ }
      }
      if (this.hasLandscapeBtnTarget) {
        this.landscapeBtnTarget.innerHTML = '<svg class="w-5 h-5 mr-1.5" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15"/></svg><span>Mode paysage</span>'
      }
    }
  }

  async _onRecordingStopped() {
    const durationSeconds = Math.round((Date.now() - this.startTime) / 1000)
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      // Upload any remaining pending chunks
      if (this.pendingChunks.length > 0) {
        this.statusTarget.textContent = "Upload des derniers segments..."
        this.progressTarget.classList.remove("hidden")

        // Force upload remaining chunks
        while (this.pendingChunks.length > 0) {
          await this._forceUploadRemainingChunks()
        }
      }

      // Retry any failed chunks from IndexedDB
      const pendingFromDB = await this.chunkStorage.getPendingChunks(this.recordingId)
      if (pendingFromDB.length > 0) {
        this.statusTarget.textContent = `Récupération de ${pendingFromDB.length} segments...`

        for (const chunk of pendingFromDB) {
          try {
            await this._uploadSingleChunk(chunk.blob, chunk.chunkIndex)
            await this.chunkStorage.markChunkUploaded(chunk.id)
            this.uploadedChunkCount++
            this._updateChunkStatus()
          } catch (e) {
            console.error(`Failed to upload recovered chunk ${chunk.chunkIndex}:`, e)
          }
        }
      }

      // Finalize the recording (server will assemble chunks)
      this.statusTarget.textContent = "Assemblage de la vidéo..."
      this.progressBarTarget.style.width = "90%"

      const isStandalone = !this.matchIdValue || this.matchIdValue === 0
      const baseUrl = isStandalone ? this.standaloneUrlValue : this.createUrlValue
      const basePath = baseUrl.replace(/\/matches\/\d+\/recordings$/, "/recordings").replace(/\/standalone$/, "")

      const finalizeResponse = await this._fetchWithTimeout(`${basePath}/${this.recordingId}/finalize_chunked`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken
        },
        body: JSON.stringify({
          duration_seconds: durationSeconds,
          total_chunks: this.uploadedChunkCount
        })
      }, 30000)

      if (finalizeResponse.ok) {
        const data = await finalizeResponse.json()
        // Clear IndexedDB chunks after successful finalization
        await this.chunkStorage.clearChunks(this.recordingId)

        this.statusTarget.innerHTML = `Enregistrement terminé ! <a href="${data.replay_url}" class="text-blue-600 underline" target="_blank">Voir le replay</a>`
        this.progressBarTarget.style.width = "100%"
      } else {
        let errMsg = `Erreur assemblage (${finalizeResponse.status})`
        try {
          const errData = await finalizeResponse.json()
          errMsg = errData.error || errMsg
        } catch (e) { /* not JSON */ }
        throw new Error(errMsg)
      }
    } catch (error) {
      console.error("Upload error:", error)
      this.statusTarget.textContent = `Erreur : ${this._errorMessage(error)}`

      // Keep chunks in IndexedDB for potential recovery
      this.statusTarget.innerHTML += `<br><span class="text-sm text-gray-500">Les segments sont sauvegardés localement pour récupération.</span>`
    }
  }

  // Force upload of remaining chunks at the end of recording
  async _forceUploadRemainingChunks() {
    if (this.pendingChunks.length === 0) return

    const chunksToUpload = [...this.pendingChunks]
    this.pendingChunks = []

    const mimeType = this._preferredMimeType()
    const chunkBlob = new Blob(chunksToUpload, { type: mimeType })
    const currentChunkIndex = this.chunkIndex++

    try {
      const dbId = await this.chunkStorage.saveChunk(this.recordingId, currentChunkIndex, chunkBlob)
      await this._uploadSingleChunk(chunkBlob, currentChunkIndex)
      await this.chunkStorage.markChunkUploaded(dbId)
      this.uploadedChunkCount++

      const progress = Math.min(80, 30 + (this.uploadedChunkCount * 5))
      this.progressBarTarget.style.width = `${progress}%`
    } catch (error) {
      console.error(`Final chunk ${currentChunkIndex} upload failed:`, error)
      // Put back for retry
      this.pendingChunks = chunksToUpload
      throw error
    }
  }

  // Wrap DirectUpload in a Promise with proper Error wrapping
  _directUpload(file, uploadUrl) {
    return new Promise((resolve, reject) => {
      const upload = new DirectUpload(file, uploadUrl, {
        directUploadWillStoreFileWithXHR: (request) => {
          request.upload.addEventListener("progress", (event) => {
            const percent = Math.round((event.loaded / event.total) * 100)
            this.progressBarTarget.style.width = `${percent}%`
            this.statusTarget.textContent = `Upload : ${percent}%`
          })
        }
      })

      upload.create((error, blob) => {
        if (error) {
          console.error("DirectUpload error detail:", error)
          // @rails/activestorage passes errors as strings, not Error objects
          reject(new Error(typeof error === "string" ? error : "Échec de l'upload vidéo"))
        } else {
          resolve(blob)
        }
      })
    })
  }

  // Fetch with timeout via AbortController (default 30s)
  _fetchWithTimeout(url, options = {}, timeoutMs = 30000) {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs)

    return fetch(url, { ...options, signal: controller.signal })
      .then(response => {
        clearTimeout(timeoutId)
        return response
      })
      .catch(error => {
        clearTimeout(timeoutId)
        if (error.name === 'AbortError') {
          throw new Error(`Timeout après ${timeoutMs / 1000}s — vérifiez votre connexion`)
        }
        throw error
      })
  }

  _preferredMimeType() {
    const types = ["video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm", "video/mp4"]
    return types.find(type => MediaRecorder.isTypeSupported(type)) || "video/webm"
  }

  // Extract a meaningful error message from various error types
  _errorMessage(error) {
    if (error instanceof Error) return error.message
    if (typeof error === "string") return error
    if (error && error.message) return error.message
    return "Erreur inconnue"
  }

  _startTimer() {
    this.startTime = Date.now()
    this.timerInterval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - this.startTime) / 1000)
      const minutes = String(Math.floor(elapsed / 60)).padStart(2, "0")
      const seconds = String(elapsed % 60).padStart(2, "0")
      this.timerTarget.textContent = `${minutes}:${seconds}`
    }, 1000)
  }

  _clearTimer() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval)
      this.timerInterval = null
    }
  }

  _stopStream() {
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop())
      this.stream = null
    }
    if (this.hasPreviewTarget && this.previewTarget.srcObject) {
      this.previewTarget.srcObject = null
    }
  }
}
